import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, RefreshCw, PlusSquare, ArrowRight, ArrowRightLeft, Palette, CheckCircle, ExternalLink, Wrench, Layers, Building, Paintbrush, Hammer, Settings } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconMap = {
  Home: Home,
  Wrench: Wrench,
  PlusSquare: PlusSquare,
  Layers: Layers,
  Building: Building,
  Paintbrush: Paintbrush,
  Hammer: Hammer,
  Settings: Settings,
  ArrowRightLeft: ArrowRightLeft,
  RefreshCw: RefreshCw,
};

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services/public`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback to empty array on error
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Building With A Deeper Purpose
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Our Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From ground-up construction to custom renovations, we deliver exceptional craftsmanship tailored to your vision.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon] || Home;
            // Alternate colors: teal, red, yellow, teal
            const colors = [
              { bg: 'bg-cyan-500', hover: 'hover:text-cyan-600' },
              { bg: 'bg-rose-500', hover: 'hover:text-rose-600' },
              { bg: 'bg-yellow-500', hover: 'hover:text-yellow-600' },
              { bg: 'bg-cyan-500', hover: 'hover:text-cyan-600' },
            ];
            const color = colors[index % 4];
            
            return (
              <div
                key={service.id}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  
                  {/* Icon Badge */}
                  <div className={`absolute top-4 left-4 w-12 h-12 ${color.bg} rounded-xl flex items-center justify-center`}>
                    {Icon && <Icon className="w-6 h-6 text-white" />}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className={`text-xl font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors`}>
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-cyan-600 font-semibold group/link"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/consultation"
            className="inline-flex items-center justify-center px-8 py-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors group"
          >
            Request Consultation
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/estimator"
            className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-semibold rounded-lg transition-colors group"
          >
            Try Our Project Estimator
            <span className="ml-2 text-lg font-bold">$</span>
          </Link>
        </div>

        {/* Design Partnership Section */}
        <div className="mt-16 bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          <div className="flex flex-col lg:flex-row items-stretch">
            {/* Left side - Logo */}
            <div className="w-full lg:w-2/5 bg-gradient-to-br from-slate-100 to-slate-200 p-8 lg:p-12 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <img 
                  src="/images/lilac-interiors-logo.webp" 
                  alt="Lilac Interiors" 
                  className="h-32 lg:h-44 w-auto"
                />
              </div>
            </div>
            
            {/* Right side - Content */}
            <div className="w-full lg:w-3/5 p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-4">
                <Palette className="w-7 h-7 text-cyan-600" />
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Collaborative Interior Design Services
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                When you hire C3 Contracting, you get a complete design-build experience. We've partnered with <strong className="text-cyan-600">Lilac Interiors</strong> to provide professional interior design and material selection services for our new build, home transformation, and major remodel clients.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6 text-sm">
                Lilac Interiors is integrally woven into our team and collaborates closely with our clients through the design process, ensuring every detail reflects your personal style and vision.
              </p>
              
              {/* Services List */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-6">
                {[
                  "Plumbing Fixtures",
                  "Electrical Fixtures",
                  "Flooring",
                  "Tiling",
                  "Cabinet Layout & Styling",
                  "Cabinet Hardware",
                  "Countertops",
                  "Paint Colors",
                  "Door & Trim Styling",
                  "Door Hardware",
                  "Bath Accessories",
                  "Accents"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 italic mb-6">
                <CheckCircle className="w-3 h-3 text-cyan-500 inline mr-1" />
                Furniture & Décor Selection (available upon request)
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/services#lilac-interiors"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://www.lilac-interiors.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-semibold rounded-lg transition-colors text-sm"
                >
                  Visit Website
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
